/**
 * Centralized Amplify client with lazy initialization
 *
 * This module provides a singleton GraphQL client that is initialized
 * only when first accessed, ensuring Amplify.configure() has been called.
 *
 * @module frontend/src/lib/amplifyClient
 */

import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

/**
 * Get or create the Amplify GraphQL client
 *
 * CRITICAL: Creates a NEW client instance on each call to ensure
 * the latest authentication token is used. This prevents UnauthorizedException
 * errors when the client is created before user login.
 *
 * IMPORTANT: Client is configured with authMode: 'userPool' to ensure authenticated
 * requests use the user's Cognito JWT token for owner-based authorization.
 *
 * @returns GraphQL client instance
 *
 * @example
 * ```typescript
 * const client = getAmplifyClient();
 * const result = await client.queries.myQuery({ input: data });
 * ```
 */
export function getAmplifyClient(): ReturnType<typeof generateClient<Schema>> {
  // CRITICAL FIX: Always create a new client instance instead of caching
  // This ensures the latest JWT token from Amplify Auth is used
  // generateClient() is lightweight and designed to be called multiple times
  return generateClient<Schema>({
    authMode: 'userPool',
  });
}

/**
 * Reset the client instance (useful for testing)
 *
 * @deprecated No longer needed - client is created fresh on each call
 * @internal
 */
export function resetClientInstance(): void {
  // No-op - kept for backward compatibility
}
